import { useState, useRef } from 'react';
import api from '../api/client';

export function useWalmartUpload(loadProducts) {
  const [showWalmartUploadPopup, setShowWalmartUploadPopup] = useState(false);
  const [selectedWalmartFile, setSelectedWalmartFile] = useState(null);
  const [parsedWalmartData, setParsedWalmartData] = useState([]);
  const [uploadWalmartStatus, setUploadWalmartStatus] = useState(null);
  const [uploadWalmartWeek, setUploadWalmartWeek] = useState('');
  const [uploadWalmartMonth, setUploadWalmartMonth] = useState('');
  const [uploadWalmartYear, setUploadWalmartYear] = useState('');
  const [walmartPeriodMode, setWalmartPeriodMode] = useState('weekly'); // 'weekly' | 'monthly'
  const walmartFileInputRef = useRef(null);

  const handleWalmartFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedWalmartFile(file);
    setParsedWalmartData([]);
    setUploadWalmartStatus({ type: 'loading', message: 'Parsing file...' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data: result } = await api.post('/api/upload-walmart-test', formData);

      const formattedData = result.preview.map(item => ({
        sku: item.sku,
        quantity: item.quantity
      }));
      setParsedWalmartData(formattedData);
      setUploadWalmartStatus({
        type: 'success',
        message: `Successfully parsed ${result.totalRows} rows, found ${result.preview.length} valid SKUs`
      });
    } catch (error) {
      if (error.response) {
        setUploadWalmartStatus({
          type: 'error',
          message: error.response.data?.error || 'Error parsing file',
          details: error.response.data?.details
        });
      } else {
        setUploadWalmartStatus({ type: 'error', message: 'Error parsing file', details: error.message });
      }
    }
  };

  const handleWalmartUploadSubmit = async () => {
    const isWeekly = walmartPeriodMode === 'weekly';

    if (!selectedWalmartFile || !uploadWalmartYear || parsedWalmartData.length === 0) {
      setUploadWalmartStatus({ type: 'error', message: 'Please select a file and fill in all required fields' });
      return;
    }
    if (isWeekly && !uploadWalmartWeek) {
      setUploadWalmartStatus({ type: 'error', message: 'Please enter week number' });
      return;
    }
    if (!isWeekly && !uploadWalmartMonth) {
      setUploadWalmartStatus({ type: 'error', message: 'Please enter month number' });
      return;
    }

    setUploadWalmartStatus({ type: 'loading', message: 'Uploading data to database...' });

    try {
      const formData = new FormData();
      formData.append('file', selectedWalmartFile);
      formData.append('year', uploadWalmartYear);

      let url;
      if (isWeekly) {
        formData.append('weeknumber', uploadWalmartWeek);
        url = '/api/upload-walmart-sales';
      } else {
        formData.append('month', uploadWalmartMonth);
        url = '/api/upload-walmart-month-sales';
      }

      const { data: result } = await api.post(url, formData);

      setUploadWalmartStatus({
        type: 'success',
        message: result.message || 'Data uploaded successfully!'
      });
      setTimeout(() => {
        if (loadProducts) loadProducts();
        closeWalmartUploadPopup();
      }, 2000);
    } catch (error) {
      if (error.response) {
        setUploadWalmartStatus({
          type: 'error',
          message: error.response.data?.error || 'Error uploading data',
          details: error.response.data?.details
        });
      } else {
        setUploadWalmartStatus({ type: 'error', message: 'Error uploading data', details: error.message });
      }
    }
  };

  const closeWalmartUploadPopup = () => {
    setShowWalmartUploadPopup(false);
    setSelectedWalmartFile(null);
    setParsedWalmartData([]);
    setUploadWalmartStatus(null);
    setUploadWalmartWeek('');
    setUploadWalmartMonth('');
    setUploadWalmartYear('');
    if (walmartFileInputRef.current) walmartFileInputRef.current.value = '';
  };

  return {
    showWalmartUploadPopup, setShowWalmartUploadPopup,
    selectedWalmartFile, setSelectedWalmartFile,
    parsedWalmartData, setParsedWalmartData,
    uploadWalmartStatus, setUploadWalmartStatus,
    uploadWalmartWeek, setUploadWalmartWeek,
    uploadWalmartMonth, setUploadWalmartMonth,
    uploadWalmartYear, setUploadWalmartYear,
    walmartPeriodMode, setWalmartPeriodMode,
    walmartFileInputRef,
    handleWalmartFileUpload,
    handleWalmartUploadSubmit,
    closeWalmartUploadPopup
  };
}